import React, { Component } from 'react';
import styled from 'styled-components';
import KpiCards from './ui/KpiCards';
import OverviewSection from './ui/OverviewSection';
import DevicesSection from './ui/DevicesSection';
import ForecastSection from './ui/ForecastSection';
import axios from 'axios';
import moment from 'moment';
import { getSummary, getChartParam, getChartSeries } from './model/dashboardCalc';
import {
  HP_DEVICES,
  DASHBOARD_TAB,
  DASHBOARD_TAB_LIST,
  PERIOD_TYPE,
  FILTER_ALL,
} from './model/heatPumpDevice';
import PS from '../PlantList/MonitoringApiService';
import { SIDO_LIST } from '../../commons/constants/sido/SIDO_LIST';

// 히트펌프 통합 관제 (로그인 후 첫 페이지)
class DashboardMain extends Component {
  constructor(props) {
    super(props);
    this.state = {
      devices: [],
      tab: DASHBOARD_TAB.OVERVIEW,
      period: PERIOD_TYPE.HOUR,
      region: FILTER_ALL,
      maker: FILTER_ALL,
      isFullscreen: false,
      carrierSummary: null, // 히트펌프 요약 (현재 소비전력, 누적 소비전력, 누적 난방 열량)
      todayPower: null, // 금일 소비전력
      sidoPowerList: [], // 지역별 누적 소비전력 [{ sidoCode, deviceCount, totalConsPower, heatingMeteringAmount, cop }]
      vendorCountList: [], // 제조사별 등록 기기 수 [{ vendor, count }]
      deviceStateCount: null, // 상태별 기기 수 { total, states: [{ state, count }] }
      powerSeries: getChartSeries(PERIOD_TYPE.HOUR), // 전력 소비 트렌드 차트 [values, labels]
      vendorList: [], // 제조사 리스트 (기기등록 히트펌프 모델 기준)
    };
    this.wrapRef = React.createRef();
  }

  componentDidMount() {
    this.getDevices();
    this.getVendorList();
    this.getCarrierData();
    this.getPowerChart();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  }

  componentDidUpdate(prevProps, prevState) {
    const { region, maker, period } = this.state;
    if (prevState.region !== region || prevState.maker !== maker) {
      this.getCarrierData();
    }
    if (prevState.region !== region || prevState.maker !== maker || prevState.period !== period) {
      this.getPowerChart();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  }

  // ESC 등으로 전체화면이 해제되는 경우까지 상태 동기화
  handleFullscreenChange = () => {
    const el = document.fullscreenElement || document.webkitFullscreenElement;
    this.setState({ isFullscreen: !!el && el === this.wrapRef.current });
  };

  // 대시보드 영역(Wrap)만 전체화면으로 전환
  handleToggleFullscreen = () => {
    const el = this.wrapRef.current;
    if (!el) return;

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }

    if (el.requestFullscreen) el.requestFullscreen().catch((e) => console.log(e));
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  // TODO: 히트펌프 기기 API 연동 (현재 시연용 샘플 데이터)
  getDevices = () => {
    this.setState({ devices: HP_DEVICES.map((d) => ({ ...d })) });
  };

  // 기기등록 화면과 동일한 히트펌프 모델 목록에서 제조사만 중복 없이 추출
  getVendorList = async () => {
    try {
      const res = await axios.get('/api/heatpump');
      const vendorList = [...new Set((res.data || []).map((hp) => hp.company).filter(Boolean))];
      this.setState({ vendorList });
    } catch (error) {
      console.error('제조사 리스트 조회 실패', error);
    }
  };

  // 대시보드 API 공통 파라미터 (전체 선택 시 해당 파라미터 미전송)
  getFilterParam = () => {
    const { region, maker } = this.state;
    return {
      sidoCode: region === FILTER_ALL ? undefined : region,
      vendor: maker === FILTER_ALL ? undefined : maker,
    };
  };

  // 히트펌프 요약 + 금일 소비전력 + 지역별 소비전력 + 제조사별 기기 수 + 상태별 기기 수 조회
  getCarrierData = () => {
    this.getCarrierSummary();
    this.getTodayPower();
    this.getTotalPowerRegion();
    this.getVendorCount();
    this.getDeviceStateCount();
  };

  // 상태별 기기 수 조회
  getDeviceStateCount = async () => {
    try {
      const res = await PS.getDashboardDeviceStateCount(this.getFilterParam());
      if (res && res.resultCode === '0') {
        this.setState({ deviceStateCount: res.data });
      }
    } catch (error) {
      console.error('상태별 기기 수 조회 실패', error);
    }
  };

  // 제조사별 운전 중(RUNNING) 기기 수 조회
  getVendorCount = async () => {
    try {
      const res = await PS.getDashboardDeviceVendorCount({ ...this.getFilterParam(), state: 'RUNNING' });
      if (res && res.resultCode === '0') {
        this.setState({ vendorCountList: Array.isArray(res.data) ? res.data : [] });
      }
    } catch (error) {
      console.error('제조사별 기기 수 조회 실패', error);
    }
  };

  // 히트펌프 요약 조회
  getCarrierSummary = async () => {
    try {
      const res = await PS.getDashboardCarrierSummary(this.getFilterParam());
      if (res && res.resultCode === '0') {
        this.setState({ carrierSummary: res.data });
      }
    } catch (error) {
      console.error('히트펌프 요약 조회 실패', error);
    }
  };

  // 금일 소비전력 조회
  getTodayPower = async () => {
    try {
      const res = await PS.getDashboardCarrierTodayPower(this.getFilterParam());
      if (res && res.resultCode === '0') {
        this.setState({ todayPower: res.data?.power });
      }
    } catch (error) {
      console.error('금일 소비전력 조회 실패', error);
    }
  };

  // 지역(시도)별 누적 소비전력 조회
  getTotalPowerRegion = async () => {
    try {
      const res = await PS.getDashboardCarrierTotalPowerRegion(this.getFilterParam());
      if (res && res.resultCode === '0') {
        this.setState({ sidoPowerList: Array.isArray(res.data) ? res.data : [] });
      }
    } catch (error) {
      console.error('지역별 소비전력 조회 실패', error);
    }
  };

  // 전력 소비 트렌드 차트 조회 (탭/필터를 빠르게 바꿀 때 이전 요청 응답은 무시)
  getPowerChart = async () => {
    const { period } = this.state;
    const now = moment();
    const reqId = (this.chartReqId = (this.chartReqId || 0) + 1);

    try {
      const res = await PS.getDashboardCarrierChart({ ...this.getFilterParam(), ...getChartParam(period, now) });
      if (reqId !== this.chartReqId) return;
      const data = res && res.resultCode === '0' ? res.data : [];
      this.setState({ powerSeries: getChartSeries(period, data, now) });
    } catch (error) {
      console.error('전력 소비 트렌드 조회 실패', error);
    }
  };

  handleRefresh = () => {
    this.getDevices();
    this.getCarrierData();
    this.getPowerChart();
  };

  handleChangeTab = (tab) => {
    this.setState({ tab });
    window.scrollTo(0, 0);
  };

  render() {
    const {
      devices,
      tab,
      period,
      region,
      maker,
      isFullscreen,
      carrierSummary,
      todayPower,
      sidoPowerList,
      vendorCountList,
      deviceStateCount,
      powerSeries,
      vendorList,
    } = this.state;
    // 지역/제조사 필터는 API 파라미터(시도코드, 제조사)로만 사용 - 시연용 샘플 데이터는 필터 미적용
    const filtered = devices;
    const summary = getSummary(filtered);
    const currentTab = DASHBOARD_TAB_LIST.find((x) => x.key === tab);

    return (
      <Wrap ref={this.wrapRef}>
        <div className="dash-shell">
          <div className="dash-top">
            {/* 전체화면일 때만 원본 시안의 NEXA 브랜드 헤더 노출 (일반 화면은 공통 Header 사용) */}
            {isFullscreen && (
              <div className="dash-brand">
                <img className="dash-logo" src="/images/dashboard/logo_nexa_dash.png" alt="Nexa" />
                <span className="brand-divider" />
                <span className="brand-tagline">Next Energy AI Platform</span>
              </div>
            )}
            <nav className="dash-nav">
              {DASHBOARD_TAB_LIST.filter((x) => !x.hidden).map((x) => (
                <button
                  key={x.key}
                  type="button"
                  className={`navbtn ${tab === x.key ? 'active' : ''}`}
                  onClick={() => this.handleChangeTab(x.key)}
                >
                  {x.label}
                </button>
              ))}
            </nav>
            <div className="dash-right">
              <span className="livepill">● 시스템 정상</span>
              <button type="button" className="fullscreen-btn" onClick={this.handleToggleFullscreen}>
                {isFullscreen ? '⤡ 전체화면 종료' : '⤢ 전체화면'}
              </button>
            </div>
          </div>

          <div className="dash-intro">
            <div>
              <div className="eyebrow">NEXA / ENERGY INTELLIGENCE / HEAT PUMP</div>
              <h1>{currentTab.title}</h1>
              <p>기기 운전, 에너지 효율, 소비전력 및 AI 수요예측을 한눈에 확인합니다.</p>
            </div>
            <div className="filters">
              <select aria-label="지역" value={region} onChange={(e) => this.setState({ region: e.target.value })}>
                <option value={FILTER_ALL}>전체 지역</option>
                {SIDO_LIST.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select aria-label="제조사" value={maker} onChange={(e) => this.setState({ maker: e.target.value })}>
                <option value={FILTER_ALL}>전체 제조사</option>
                {vendorList.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <button type="button" onClick={this.handleRefresh}>
                ↻ 새로고침
              </button>
            </div>
          </div>

          <KpiCards carrierSummary={carrierSummary} todayPower={todayPower} deviceStateCount={deviceStateCount} />

          {tab === DASHBOARD_TAB.OVERVIEW && (
            <OverviewSection
              devices={filtered}
              summary={summary}
              period={period}
              powerSeries={powerSeries}
              sidoPowerList={sidoPowerList}
              vendorCountList={vendorCountList}
              onChangePeriod={(p) => this.setState({ period: p })}
            />
          )}
          {tab === DASHBOARD_TAB.DEVICES && (
            <div className="section">
              <DevicesSection devices={filtered} />
            </div>
          )}
          {tab === DASHBOARD_TAB.FORECAST && (
            <div className="section">
              <ForecastSection />
            </div>
          )}
        </div>
      </Wrap>
    );
  }
}

// #main 의 전역 padding-bottom(70px, frame.css) 을 상쇄해 푸터와의 흰 공백 제거
const Wrap = styled.div`
  min-height: 100%;
  margin-bottom: -70px;
  background: #080e1a;
  color: #edf3fc;
  padding: 0 0 20px;
  font-family: system-ui, -apple-system, 'Segoe UI', 'Malgun Gothic', sans-serif;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  /* 전체화면: 화면 크기에 맞추고 내부 스크롤 */
  &:fullscreen {
    width: 100%;
    height: 100%;
    margin: 0;
    overflow-y: auto;
  }
  &:-webkit-full-screen {
    width: 100%;
    height: 100%;
    margin: 0;
    overflow-y: auto;
  }
  /* 전체화면: 최대 폭(1600px) 제한 해제 → 좌우 화면 폭에 맞춰 확장 */
  &:fullscreen .dash-shell {
    max-width: none;
  }
  &:-webkit-full-screen .dash-shell {
    max-width: none;
  }
  &::backdrop {
    background: #080e1a;
  }

  .dash-shell {
    max-width: 1600px;
    margin: auto;
    padding: 0 32px;
  }
  .dash-top {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    padding: 17px 0;
    border-bottom: 1px solid #263248;
  }
  .dash-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    align-items: center;
    min-width: 0;
  }
  .navbtn {
    background: transparent;
    color: #91a5be;
    border: 0;
    padding: 11px 16px;
    border-radius: 9px;
    cursor: pointer;

    &.active {
      color: #eff6ff;
      background: #1b2b43;
    }
  }
  .dash-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex-shrink: 0;
  }
  .dash-logo {
    height: 44px;
    width: 98px;
    object-fit: contain;
    mix-blend-mode: screen;
    flex-shrink: 0;
  }
  .brand-divider {
    display: block;
    flex: 0 0 1px;
    width: 1px;
    height: 26px;
    background: linear-gradient(transparent, #63758d, transparent);
  }
  .brand-tagline {
    font-family: Arial, 'Noto Sans KR', sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.025em;
    color: #9aaac0;
    white-space: nowrap;
  }
  .dash-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .fullscreen-btn {
    background: #172940;
    border: 1px solid #35506b;
    color: #d5eaff;
    border-radius: 9px;
    padding: 8px 14px;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      background: #264263;
    }
  }
  .livepill {
    color: #63e7b0;
    background: #0e2b26;
    border: 1px solid #225f4d;
    border-radius: 22px;
    padding: 8px 12px;
    font-size: 12px;
  }
  .dash-intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    margin: 30px 0 22px;

    h1 {
      font-size: 29px;
      font-weight: 700;
      margin: 7px 0 6px;
      letter-spacing: -1px;
      color: #edf3fc;
    }
    p {
      color: #8fa2be;
      font-size: 13px;
      margin: 0;
    }
  }
  .eyebrow {
    color: #65bbff;
    font-size: 11px;
    letter-spacing: 2px;
    font-weight: 700;
  }
  .filters {
    display: flex;
    gap: 8px;

    select,
    button {
      background: #131f32;
      border: 1px solid #2a3a51;
      color: #d7e3f5;
      border-radius: 9px;
      padding: 10px 12px;
      font-size: 12px;
      cursor: pointer;
      height: auto;
    }
  }
  .section {
    margin-top: 14px;
  }

  @media (max-width: 850px) {
    .brand-divider,
    .brand-tagline {
      display: none;
    }
  }
  @media (max-width: 750px) {
    .dash-shell {
      padding: 0 15px;
    }
    .dash-intro {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  @media (max-width: 700px) {
    .dash-shell {
      padding: 0 14px;
    }
    .dash-nav {
      width: 100%;
      flex-wrap: nowrap;
      overflow-x: auto;
      gap: 6px;
    }
    .navbtn {
      flex: 1 0 auto;
      white-space: nowrap;
      padding: 11px 12px;
      font-size: 12px;
    }
    .dash-intro {
      margin: 18px 0 16px;
      gap: 13px;

      h1 {
        font-size: 23px;
      }
      p {
        font-size: 12px;
        line-height: 1.5;
      }
    }
    .filters {
      width: 100%;
      flex-wrap: wrap;
      gap: 7px;

      select {
        flex: 1;
        min-width: 0;
        max-width: calc(50% - 4px);
      }
      button {
        flex: 1 1 100%;
      }
    }
  }
`;

export default DashboardMain;
